// The optimised voicings rendered on the SAME lead-sheet grid as the chord
// score, so this view is laid out identically to the chart: section marks,
// barlines, repeat/simile signs and time signatures all mirror the score, with
// a chord symbol + a diagram in each bar.
//
// The diagram follows the comp INSTRUMENT (#236): a guitar chord carries a
// grip and draws the fretboard; a piano chord additionally carries `midis` (+
// `hands`/`held` from the voiced walk) and draws the keyboard strip instead —
// so what the sheet shows is what the comp channel plays. Every keyboard on
// the sheet shares ONE range (octave-snapped over all pitches drawn), or the
// same C would sit in a different place on every row and the voice leading
// would be unreadable as motion.

import { useMemo } from 'react';
import { topNoteOf } from '@/music/voicing';
import type { SequenceChord } from '@/music/song';
import type { OptimizedChord } from '@/music/voiceLeading';
import type { IRealChart } from '@/music/ireal/types';
import { sequenceChordIndex } from '@/music/chart';
import { getChordPlayer } from '@/audio/player';
import { ChartGrid } from './ChartView';
import { useChartCells, useDiagramSlotChords } from './chartLayout';
import { ChartRoman, ChordSymbol } from './chartChrome';
import { chartMarkKey, type ChartAnalysisMark } from './chartAnalysis';
import { PlayableDiagram } from './PlayableDiagram';
import { PianoKeyboardDiagram } from './PianoKeyboardDiagram';
import { useInspector, type InspectPinContext } from './inspectorContext';
import { pinLane, type PinPlan } from '@/music/voicingPins';
import { effectivePianoFamily } from '@/music/pianoLeftHand';
import { useStore } from '@/state/store';

type OptimizedSeqChord = OptimizedChord<SequenceChord>;

/** A sheet chord: the grip always, plus the piano realization when the comp
 * channel is a keyboard (attached by the page from `band.pianoOptimized`). */
type SheetChord = OptimizedSeqChord & {
  midis?: number[];
  hands?: { left: number[]; right: number[] };
  /** Which triad the right hand plays (piano upper-structure family only). */
  upperTriad?: { root: string; quality: 'major' | 'minor' };
  /** The user's own stack, when the piano walk played a pin (voicingPins.ts). */
  pinnedVoicing?: { midis: number[] };
  /** Melody-led plans carry the written top; the pin picker reads it. */
  targetTopMidi?: number | null;
  voicingNotes?: string[];
};

interface GuitarChartViewProps {
  chart: IRealChart;
  /** Same scale as the score overlay; also controls chart reflow. */
  scale?: number;
  /** Optimised voicings for each authored measure (first pass through the form). */
  byMeasure: Map<string, SheetChord[]>;
  playingMeasureId?: string | null;
  ariaLabel?: string;
  passageMeasureIds?: ReadonlySet<string>;
  onSelectPassage?: (fromId: string, toId: string, opts?: { extend?: boolean }) => void;
  onPassageGesture?: () => void;
  /** A tap on a bar outside its diagrams: the start cursor. */
  onTapMeasure?: (id: string) => void;
  cursorMeasureId?: string | null;
  /** Page view overrides: density from the PAGE the sheet spans, and the
   * layout-row range this instance renders (see ChartGridProps). */
  cellsPerRow?: number;
  /** Grips per bar slot at the page's width (PagedSheet); the viewport's
   * otherwise. */
  slotChords?: number;
  rowWindow?: readonly [number, number];
  /** The analysis lane's marks by measure id + chord index (chartAnalysis.ts). */
  analysisMarks?: ReadonlyMap<string, ChartAnalysisMark>;
  /** Which plan the grips come from — the lane a pin made here belongs to
   * (voicingPins.ts). The classic voicings sheet is the comping plan; the
   * chord-melody diagram sheet passes 'melody'. */
  pinPlan?: PinPlan;
}

/** A diagram lead sheet — the score's twin, voicings instead of symbols. */
export function GuitarChartView({
  chart,
  scale,
  byMeasure,
  playingMeasureId,
  ariaLabel = 'Guitar chord diagrams',
  passageMeasureIds,
  onSelectPassage,
  onPassageGesture,
  onTapMeasure,
  cursorMeasureId,
  cellsPerRow: cellsOverride,
  slotChords: slotOverride,
  rowWindow,
  analysisMarks,
  pinPlan = 'comping',
}: GuitarChartViewProps) {
  const viewportCells = useChartCells(chart, scale);
  const cellsPerRow = cellsOverride ?? viewportCells;
  const viewportSlots = useDiagramSlotChords(cellsPerRow, scale);
  const slotChords = slotOverride ?? viewportSlots;
  // Whether this sheet draws keyboards at all (false = guitar sheet). Each
  // diagram takes its own COMPACT window — the iReal reading (user ruling
  // Aug 2026): a whole-sheet shared range squeezed the tune's full span into
  // every cell, which on a phone meant tiny unreadable keys. Since Sep 2026
  // the window is trimmed to the voicing plus two white keys either side
  // (PianoKeyboardDiagram's KEY_PAD), so the strip's width says how wide the
  // voicing is, and the grey middle C carries the octave orientation where it
  // falls in range.
  const hasPiano = useMemo(
    () => [...byMeasure.values()].some((chords) => chords.some((chord) => chord.midis)),
    [byMeasure],
  );
  return (
    <ChartGrid
      chart={chart}
      // `short-meter` disables the lone-chord half-slot rule: a 2/4 bar is
      // already the width of a 4/4 half-slot, so halving it AGAIN shrank
      // Lôro's grips to postage stamps under full-size symbols.
      gridClassName={`optimized-grid optimized-chart-grid diagram-sheet${
        chart.timeSignature[0] <= 2 ? ' short-meter' : ''
      }${hasPiano ? ' piano-sheet' : ''}${slotChords < 2 ? ' one-grip-slots' : ''}`}
      measureBaseClassName="optimized-measure"
      ariaLabel={ariaLabel}
      cellsPerRow={cellsPerRow}
      slotChords={slotChords}
      rowWindow={rowWindow}
      playingMeasureId={playingMeasureId}
      passageMeasureIds={passageMeasureIds}
      onSelectPassage={onSelectPassage}
      onPassageGesture={onPassageGesture}
      onTapMeasure={onTapMeasure}
      cursorMeasureId={cursorMeasureId}
      renderBody={(m) => {
        const chords = byMeasure.get(m.id);
        if (!chords || chords.length === 0) return null;
        const playing = !!playingMeasureId && m.id === playingMeasureId;
        return chords.map((c) => {
          // Marks are keyed by the chord's index among the bar's WRITTEN chords;
          // this list holds only the voiced ones, so an N.C. ahead of a chord
          // would shift every later mark onto the wrong diagram.
          const ci = analysisMarks ? sequenceChordIndex(c.id) : null;
          return (
            <DiagramCell
              key={c.id}
              chord={c}
              playing={playing}
              piano={hasPiano}
              mark={ci == null ? undefined : analysisMarks?.get(chartMarkKey(m.id, ci))}
              pinPlan={pinPlan}
            />
          );
        });
      }}
    />
  );
}

/** A cell's flex-grow: the chord's beats in THIS bar, as the chord score
 * sizes its cells. The written span, not `durationBeats`, which carries a
 * bar's last chord on through any empty bars after it. */
const cellGrow = (chord: SheetChord) =>
  Math.max(1, chord.writtenBeats ?? (chord.durationBeats || 1));

/** The chord as the engine saw it — what the pin picker enumerates from. */
function pinChordOf(chord: SheetChord) {
  return {
    root: chord.root,
    displayRoot: chord.displayRoot,
    chordType: chord.chordType,
    targetTopNote: chord.targetTopNote,
    targetTopMidi: chord.targetTopMidi,
    voicingNotes: chord.voicingNotes,
    ...(chord.bass ? { bass: chord.bass } : {}),
  };
}

function DiagramCell({
  chord,
  playing,
  piano,
  mark,
  pinPlan,
}: {
  chord: SheetChord;
  playing: boolean;
  piano: boolean;
  mark?: ChartAnalysisMark;
  pinPlan: PinPlan;
}) {
  const { inspect } = useInspector();
  const fretNumberScale = useStore((s) => s.arrangeFretNumberScale);
  const diagramMarkers = useStore((s) => s.arrangeDiagramMarkers);
  const compInstrument = useStore((s) => s.compInstrument);
  const compPianoFamily = useStore((s) => s.compPianoFamily);
  if (chord.midis && piano) {
    const midis = chord.midis;
    const pinned = !!chord.pinnedVoicing;
    const pin: InspectPinContext | undefined = chord.pinKey
      ? {
          lane: pinLane(pinPlan, 'piano'),
          key: chord.pinKey,
          chord: pinChordOf(chord),
          pinned,
          pianoFamily: effectivePianoFamily(compInstrument, compPianoFamily),
        }
      : undefined;
    return (
      <div className="diagram-cell" style={{ flexGrow: cellGrow(chord) }}>
        <ChordSymbol className="diagram-symbol" symbol={chord.symbol} />
        {mark && <ChartRoman mark={mark} className="diagram-roman" />}
        <div
          className={`optimized-chord piano-chord${playing ? ' playing' : ''}${pinned ? ' pinned' : ''}`}
          data-pinned={pinned || undefined}
          onDoubleClick={() =>
            inspect({
              kind: 'piano',
              midis,
              hands: chord.hands,
              rootDisplay: chord.displayRoot,
              chordType: chord.chordType,
              symbol: chord.symbol,
              pin,
            })
          }
        >
          <PianoKeyboardDiagram
            midis={midis}
            // No shared range: each chord's own trimmed window (see the
            // hasPiano note above). No size either — `.piano-chord svg`
            // fixes the height and the width follows the window's aspect.
            label={`${chord.symbol} piano voicing`}
            onClick={() => {
              try {
                void getChordPlayer().playMidi(midis, 0.012, 'piano');
              } catch {
                /* no audio */
              }
            }}
          />
          {chord.upperTriad && (
            <div className="piano-upper-triad" data-testid="sheet-upper-triad">
              {chord.upperTriad.root}
              {chord.upperTriad.quality === 'minor' ? 'm' : ''} / {chord.symbol}
            </div>
          )}
        </div>
      </div>
    );
  }
  const lead = topNoteOf(chord.fingering, chord.stringSet);
  const pinned = !!chord.pin;
  return (
    // Cell width ∝ the chord's beat duration, so a half-note chord's slot is
    // twice a quarter-note's. The diagram starts on the chord's beat; its label
    // is centred over the diagram rather than over the chord's held duration.
    <div className="diagram-cell" style={{ flexGrow: cellGrow(chord) }}>
      <ChordSymbol className="diagram-symbol" symbol={chord.symbol} />
      {mark && <ChartRoman mark={mark} className="diagram-roman" />}
      <PlayableDiagram
        fretNumberScale={fretNumberScale}
        markers={diagramMarkers}
        variant="bare"
        showTitle={false}
        className={`optimized-chord${playing ? ' playing' : ''}${pinned ? ' pinned' : ''}`}
        chord={{
          fingering: chord.fingering,
          rootDisplay: chord.displayRoot,
          chordType: chord.chordType,
          symbol: chord.symbol,
          inversion: chord.inversion,
          stringSet: chord.stringSet,
          leadNote: lead,
          targetTopNote: chord.targetTopNote,
          ...(chord.pinKey
            ? {
                pin: {
                  lane: pinLane(pinPlan, 'guitar'),
                  key: chord.pinKey,
                  chord: pinChordOf(chord),
                  pinned,
                },
              }
            : {}),
        }}
      />
    </div>
  );
}
